import { formatDateForList } from '../../utils'
import {
  getColumnsModel,
  getConfigOptions,
  mapObjectToLabels,
  mapValueToLabel,
} from '../Statistics/utils'
import { BASIC_ORG } from './merge-constants'
import { CRASH_ID_KEY } from './utils'
import {
  collectPeopleFromCrash,
  getPersonKey,
  matchesCandidate,
} from './match-display-rows'

/** Colonne legacy (griglia piatta). */
export const MATCH_GRID_FIELD_KEYS = [
  'Unit id',
  CRASH_ID_KEY,
  'Crash date',
  'Crash type',
  'Type',
  'Vehicle registration year',
  'Gender',
  'Name',
  'Surname',
]

/** Campi mostrati nella riga principale (collapse header). */
export const CRASH_SUMMARY_FIELD_KEYS = [
  CRASH_ID_KEY,
  'Crash date',
  'Crash type',
]

/** Campi dettaglio unità (Police / Insurance). */
export const UNIT_DETAIL_FIELD_KEYS = [
  'Unit id',
  'Type',
  'Vehicle registration year',
]

/** Campi dettaglio persona (Police / Insurance) — senza Unit id: si vede nel gruppo unità. */
export const PERSON_DETAIL_FIELD_KEYS = [
  'Person id',
  'Gender',
  'Name',
  'Surname',
]

/** Campi dettaglio persona (Hospital). */
export const HOSPITAL_PERSON_DETAIL_FIELD_KEYS = [
  'Person id',
  'Gender',
  'Name',
  'Surname',
  'Date of admission',
  'Date of exit',
  'Injury severity',
  'Type of injury',
]

const RECORD_META_KEYS = new Set([
  'id',
  'crashSpan',
  'images',
  'candidate_name',
  'candidate_surname',
  'merge_date',
  'user_id',
  'userId',
])

/**
 * Allinea alias campo API (es. `unit id` → `Unit id`).
 * @param {Record<string, unknown>} row
 */
export function canonicalizeFieldKeys(row) {
  if (!row || typeof row !== 'object') return {}
  const result = { ...row }
  if (result['unit id'] !== undefined && result['Unit id'] === undefined) {
    result['Unit id'] = result['unit id']
  }
  return result
}

/**
 * Normalizza crash nested (`{ crash, units, people }`) e flat `format=table`
 * (`Crash id` in root) nello stesso schema.
 * @param {Record<string, unknown>} raw
 */
export function normalizeCrashRecord(raw) {
  if (!raw || typeof raw !== 'object') return null

  const nestedCrash = raw.crash
  const hasNestedCrash =
    nestedCrash &&
    typeof nestedCrash === 'object' &&
    nestedCrash[CRASH_ID_KEY] != null

  if (hasNestedCrash) {
    return {
      ...raw,
      road: raw.road ?? {},
      people: raw.people ?? [],
      units: raw.units ?? (raw.unit ? [raw.unit] : []),
      crash: nestedCrash,
    }
  }

  const hasTopLevelCrashId = raw[CRASH_ID_KEY] != null
  if (hasTopLevelCrashId) {
    const crash = {}
    const road = raw.road ?? {}
    let people = raw.people ?? []
    let units = raw.units ?? (raw.unit ? [raw.unit] : [])

    Object.entries(raw).forEach(([key, value]) => {
      if (key === 'road' || key === 'people' || key === 'unit' || key === 'units') return
      if (RECORD_META_KEYS.has(key)) return
      crash[key] = value
    })

    return {
      road,
      people,
      units,
      crash,
      ...(raw.images ? { images: raw.images } : {}),
      ...(raw.candidate_name ? { candidate_name: raw.candidate_name } : {}),
      ...(raw.candidate_surname ? { candidate_surname: raw.candidate_surname } : {}),
    }
  }

  return raw
}

/**
 * @param {unknown} collection
 * @returns {unknown[]}
 */
function asArray(collection) {
  if (collection == null) return []
  if (Array.isArray(collection)) return collection
  if (typeof collection === 'object') return Object.values(collection)
  return []
}

/**
 * Sezioni dettaglio per tipo organizzazione.
 * @param {number | undefined} organizationBasic
 */
export function getMatchDetailSections(organizationBasic) {
  const basic = Number(organizationBasic)
  if (basic === BASIC_ORG.HOSPITAL) {
    return { showUnits: false, showPeople: true }
  }
  return { showUnits: true, showPeople: true }
}

/**
 * @param {number | undefined} organizationBasic
 * @returns {string[]}
 */
export function getPersonDetailFieldKeys(organizationBasic) {
  if (Number(organizationBasic) === BASIC_ORG.HOSPITAL) {
    return HOSPITAL_PERSON_DETAIL_FIELD_KEYS
  }
  return PERSON_DETAIL_FIELD_KEYS
}

/**
 * @param {object} configuration
 * @param {string[]} fieldKeys
 * @param {{ extraColumns?: object[] }} [options]
 */
export function getMatchColumnsForFields(configuration, fieldKeys, options = {}) {
  const columns = getMatchGridColumns(configuration, options)
  const ordered = fieldKeys
    .map((field) => columns.find((col) => col.field === field))
    .filter(Boolean)
  if (ordered.length > 0) return ordered
  return fieldKeys.map((field) => ({
    field,
    headerName: field,
    flex: 1,
    minWidth: field === 'Crash date' ? 160 : field === 'Person id' ? 88 : 110,
    ...(field === 'Person id'
      ? { headerName: 'Person id' }
      : {}),
    ...( /\bdate\b/i.test(field)
      ? {
          valueFormatter: (paramsOrValue) => {
            const v = paramsOrValue?.value ?? paramsOrValue
            return formatDateForList(v)
          },
        }
      : {}),
  }))
}

/**
 * @param {Record<string, unknown>} mappedCrash
 * @param {Record<string, unknown>} configOptions
 */
function buildCrashBaseFromMapped(mappedCrash, configOptions) {
  return applyConfigLabelsToRow(
    canonicalizeFieldKeys({
      ...(mappedCrash?.road ?? {}),
      ...(mappedCrash?.crash ?? {}),
    }),
    configOptions,
  )
}

/**
 * Colonne piatte per griglie match: niente grouping, niente row spanning.
 * @param {object} configuration
 * @param {{ extraColumns?: object[] }} [options]
 */
export function getMatchGridColumns(configuration, options = {}) {
  const { columns: allColumns } = getColumnsModel(configuration ?? {})

  let baseColumns = allColumns
    .filter((col) => MATCH_GRID_FIELD_KEYS.includes(col.field))
    .sort(
      (a, b) =>
        MATCH_GRID_FIELD_KEYS.indexOf(a.field) - MATCH_GRID_FIELD_KEYS.indexOf(b.field),
    )

  if (baseColumns.length === 0) {
    baseColumns = MATCH_GRID_FIELD_KEYS.map((field) => ({
      field,
      headerName: field,
      flex: 1,
      minWidth: field === 'Crash date' ? 160 : 110,
    }))
  }

  baseColumns = baseColumns.map((col) => {
    const next = { ...col, flex: 1, minWidth: col.minWidth ?? 110 }
    delete next.rowSpanValueGetter
    if (/\bdate\b/i.test(col.field ?? '')) {
      next.valueFormatter = (paramsOrValue) => {
        const v = paramsOrValue?.value ?? paramsOrValue
        return formatDateForList(v)
      }
    }
    return next
  })

  return [...baseColumns, ...(options.extraColumns ?? [])]
}

/**
 * @param {Record<string, unknown>} row
 * @param {Record<string, unknown>} configOptions
 */
export function applyConfigLabelsToRow(row, configOptions) {
  const labeled = { ...row }
  Object.keys(labeled).forEach((key) => {
    const value = labeled[key]
    if (value === null || value === undefined) return
    if (typeof value === 'object') return
    labeled[key] = mapValueToLabel(key, value, configOptions)
  })
  return labeled
}

/**
 * @param {Record<string, unknown>} [storedPeople]
 * @param {string} personKey
 */
function readStoredIncludeFlag(storedPeople, personKey) {
  if (!Array.isArray(storedPeople)) return undefined
  const stored = storedPeople.find((p) => getPersonKey(p) === personKey)
  if (!stored || stored.includeInMerge === undefined) return undefined
  return stored.includeInMerge !== false
}

/**
 * @param {Record<string, unknown>} record
 * @returns {string | number | null}
 */
export function getCrashIdFromRecord(record) {
  if (!record || typeof record !== 'object') return null
  const normalized = normalizeCrashRecord(record)
  if (normalized?.crash?.[CRASH_ID_KEY] != null) {
    return normalized.crash[CRASH_ID_KEY]
  }
  return record?.crash?.[CRASH_ID_KEY] ?? record?.[CRASH_ID_KEY] ?? null
}

/**
 * Un record per crash id (l'API può restituire più righe per lo stesso incidente).
 * @param {Record<string, unknown>[]} records
 */
export function deduplicateMatchRecordsByCrashId(records) {
  if (!Array.isArray(records)) return []

  const groups = new Map()
  const order = []

  records.forEach((record, index) => {
    const crashId = getCrashIdFromRecord(record)
    const key = crashId != null ? String(crashId) : `__row_${index}`

    if (!groups.has(key)) {
      groups.set(key, [])
      order.push(key)
    }
    groups.get(key).push(record)
  })

  return order.map((key) => {
    const group = groups.get(key) ?? []
    if (group.length <= 1) return group[0]

    return {
      ...group[0],
      _matchMetas: group,
    }
  })
}

/**
 * @param {Record<string, unknown>} normalized
 */
function resolveCrashId(normalized, mappedCrash) {
  return (
    mappedCrash?.crash?.[CRASH_ID_KEY] ??
    normalized?.crash?.[CRASH_ID_KEY] ??
    mappedCrash?.[CRASH_ID_KEY] ??
    normalized?.[CRASH_ID_KEY] ??
    null
  )
}

/**
 * Costruisce righe griglia per un crash/match (una riga per persona).
 * @param {Record<string, unknown>} rawCrash
 * @param {object} configuration
 * @param {{ matchIndex?: number, matchMeta?: Record<string, unknown>, storedPeople?: Record<string, unknown>[] }} [options]
 */
export function buildMatchGridRows(rawCrash, configuration, options = {}) {
  if (!rawCrash || typeof rawCrash !== 'object') return []

  const matchIndex = options.matchIndex ?? 0
  const matchMeta = options.matchMeta ?? rawCrash
  const configOptions = getConfigOptions(configuration ?? {})
  const normalized = normalizeCrashRecord(rawCrash)
  if (!normalized) return []

  const mappedCrash = mapObjectToLabels(normalized, configOptions)
  const crashId = resolveCrashId(normalized, mappedCrash)
  const crashBase = buildCrashBaseFromMapped(mappedCrash, configOptions)
  const rawPeople = collectPeopleFromCrash(normalized)

  if (rawPeople.length === 0) {
    return [
      {
        ...crashBase,
        [CRASH_ID_KEY]: crashId,
        isMatchCandidate: true,
        includeInMerge: true,
        personKey: null,
        id: `${crashId ?? 'crash'}.${matchIndex}.0`,
      },
    ]
  }

  return rawPeople.map((rawPerson) => {
    const { unit, unitIndex, personIndex, ...personOnly } = rawPerson
    const personKey = getPersonKey(rawPerson)
    const isCandidate = matchesCandidate(personOnly, matchMeta)
    const storedInclude = readStoredIncludeFlag(options.storedPeople, personKey)

    const mappedPerson = mapObjectToLabels(personOnly, configOptions)
    const unitWithoutPeople = unit
      ? Object.fromEntries(Object.entries(unit).filter(([key]) => key !== 'people'))
      : null
    const mappedUnit = unitWithoutPeople
      ? canonicalizeFieldKeys(mapObjectToLabels(unitWithoutPeople, configOptions))
      : {}

    return {
      ...crashBase,
      ...mappedUnit,
      ...mappedPerson,
      [CRASH_ID_KEY]: crashId,
      isMatchCandidate: isCandidate,
      includeInMerge: storedInclude ?? isCandidate,
      personKey,
      id: `${crashId ?? 'crash'}.${matchIndex}.${personKey}`,
    }
  })
}

/**
 * Costruisce un gruppo crash per vista collapse (summary + units + people).
 * @param {Record<string, unknown>} rawCrash
 * @param {object} configuration
 * @param {{ matchIndex?: number, matchMeta?: Record<string, unknown>, storedPeople?: Record<string, unknown>[], organizationBasic?: number }} [options]
 */
export function buildMatchGroup(rawCrash, configuration, options = {}) {
  if (!rawCrash || typeof rawCrash !== 'object') return null

  const matchIndex = options.matchIndex ?? 0
  const matchMeta = options.matchMeta ?? rawCrash
  const configOptions = getConfigOptions(configuration ?? {})
  const normalized = normalizeCrashRecord(rawCrash)
  if (!normalized) return null

  const mappedCrash = mapObjectToLabels(normalized, configOptions)
  const crashId = resolveCrashId(normalized, mappedCrash)
  const crashBase = buildCrashBaseFromMapped(mappedCrash, configOptions)
  const summary = CRASH_SUMMARY_FIELD_KEYS.reduce((acc, key) => {
    if (crashBase[key] !== undefined && crashBase[key] !== null && crashBase[key] !== '') {
      acc[key] = crashBase[key]
    }
    return acc
  }, /** @type {Record<string, unknown>} */ ({}))
  if (crashId != null) summary[CRASH_ID_KEY] = crashId

  const units = asArray(normalized.units).map((unit, unitIndex) => {
    const withoutPeople = Object.fromEntries(
      Object.entries(unit ?? {}).filter(([key]) => key !== 'people'),
    )
    const mappedUnit = applyConfigLabelsToRow(
      canonicalizeFieldKeys(mapObjectToLabels(withoutPeople, configOptions)),
      configOptions,
    )
    return {
      ...mappedUnit,
      unitIndex,
      id: `${crashId ?? 'crash'}.${matchIndex}.unit.${unitIndex}`,
    }
  })

  const rawPeople = collectPeopleFromCrash(normalized)
  const people =
    rawPeople.length === 0
      ? [
          {
            id: `${crashId ?? 'crash'}.${matchIndex}.person.0`,
            personKey: null,
            unitIndex: null,
            isMatchCandidate: true,
            includeInMerge: true,
          },
        ]
      : rawPeople.map((rawPerson) => {
          const { unit, unitIndex, personIndex, ...personOnly } = rawPerson
          const personKey = getPersonKey(rawPerson)
          const isCandidate = matchesCandidate(personOnly, matchMeta)
          const storedInclude = readStoredIncludeFlag(options.storedPeople, personKey)
          const mappedPerson = applyConfigLabelsToRow(
            mapObjectToLabels(personOnly, configOptions),
            configOptions,
          )
          const mappedUnitRow = unit
            ? applyConfigLabelsToRow(
                canonicalizeFieldKeys(
                  mapObjectToLabels(
                    Object.fromEntries(
                      Object.entries(unit).filter(([key]) => key !== 'people'),
                    ),
                    configOptions,
                  ),
                ),
                configOptions,
              )
            : null

          return {
            ...mappedPerson,
            ...(mappedUnitRow?.['Unit id'] !== undefined ? { 'Unit id': mappedUnitRow['Unit id'] } : {}),
            ...(mappedUnitRow?.Type !== undefined ? { unitType: mappedUnitRow.Type } : {}),
            unitIndex: unitIndex ?? null,
            personIndex: personIndex ?? null,
            personKey,
            isMatchCandidate: isCandidate,
            includeInMerge: storedInclude ?? isCandidate,
            id: `${crashId ?? 'crash'}.${matchIndex}.person.${personKey}`,
          }
        })

  return {
    id: `${crashId ?? 'crash'}.${matchIndex}`,
    crashId,
    matchIndex,
    summary,
    units,
    people,
    unitCount: units.length,
    peopleCount: people.length,
  }
}

/**
 * Raggruppa le persone sotto l'unità di appartenenza (modello Police: units[].people).
 * @param {Record<string, unknown>[]} units
 * @param {Record<string, unknown>[]} people
 */
export function groupPeopleByUnit(units, people) {
  const groups = units.map((unit) => ({
    unit,
    people: people.filter(
      (person) =>
        person.unitIndex === unit.unitIndex ||
        (person.unitIndex == null &&
          person['Unit id'] != null &&
          person['Unit id'] === unit['Unit id']),
    ),
  })).filter((group) => group.people.length > 0)

  const assigned = new Set(groups.flatMap((group) => group.people.map((p) => p.id)))
  const orphans = people.filter((person) => !assigned.has(person.id))

  return { groups, orphans }
}

/**
 * @param {Record<string, unknown>[]} records
 * @param {object} configuration
 * @param {{ organizationBasic?: number, getStoredPeople?: (record: Record<string, unknown>, matchIndex: number) => Record<string, unknown>[] | undefined }} [options]
 */
export function buildMatchGroups(records, configuration, options = {}) {
  if (!Array.isArray(records)) return []

  const uniqueRecords = deduplicateMatchRecordsByCrashId(records)

  return uniqueRecords
    .map((record, matchIndex) =>
      buildMatchGroup(record, configuration, {
        matchIndex,
        matchMeta: record,
        organizationBasic: options.organizationBasic,
        storedPeople: options.getStoredPeople?.(record, matchIndex),
      }),
    )
    .filter(Boolean)
}

